import _ from 'lodash'
import Backbone from 'backbone'
import { TweenMax, Back, Linear, TimelineMax } from 'gsap'
import template from './dataset-building.hbs'
import Dataset from '../model/dataset'
import datasetStatus from './dataset-status'
import getErrorMessage from 'scoreboard/datasets/not-available/get-error-message'

var DatasetBuilding = Backbone.View.extend({
	className: 'dataset-building display-pane-body',

	/** @type {Dataset} */
	model: null,

	/**
	 * @param options
	 * @param {Dataset} options.model
	 * @param options.data.isQuickset is true when the dataset-building should be displayed and set to
	 * its fill level without transition/animation. Used when switching between multiple loading dataset-building animations
	 * @param {Boolean=false} options.triggerReadyEvent normally, we only trigger a ready event
	 * from this view on the first build of the dataset, but there are other scenarios we
	 * want to do this such as a query error see #47518 and {@link dataset-not-ready.js#options.forceNotReadyView}
	 */
	initialize: function(options) {
		this.options = options || {}
		datasetStatus().then(status => {
			this.listenTo(status, 'change', props => {
				// #55650 #56329
				if (props.id !== this.model.get('rootDatasetId')) return

				if (props.status === Dataset.ERROR) {
					this.setState(props.status)
					this.model.set('errorCode', props.errorCode)
					// set error arguments from the websocket so the error message
					// displays properly on the initial build.
					if (!_.isEmpty(props.errorArguments)) {
						this.model.set('errorArguments', props.errorArguments)
					}
					this.$el.empty()
					this.render()
				} else if (props.status === Dataset.READY &&
					// on initial build, we don't get numTracked back with the call to
					// the /query endpoint, so this will be hit only on the first build
					(!this.model.isDisplayable()  ||
						// #47518 if the not ready view was forced (possibly because of a query error)
						// then we will need to trigger a ready event
						this.options.triggerReadyEvent)) {

					this.trigger('ready', { progress: props.progress, status: props.status})
				}
			})
		})
	},

	render: function() {
		this.id = _.uniqueId('dataset_building_')
		this.$el.toggleClass('is-quickset', this.options.isQuickset)
		if (this.options.queryError) {
			this.setState(Dataset.LOADING)
		}

		this.$el.append(template(this.context()))
		return this
	},

	context: function() {
		return {
			id: this.id,
			error: getErrorMessage(this.model)
		}
	},

	init: function() {
		var progressTl = new TimelineMax({paused:false}).timeScale(1)
		, waveTl = new TimelineMax({repeat:-1, paused:false}).timeScale(1)
		, leftFaceWave = this.$('.leftFaceWave')
		, leftBackFaceWave = this.$('.leftBackFaceWave')
		, rightFaceWave = this.$('.rightFaceWave')
		, rightBackFaceWave = this.$('.rightBackFaceWave')
		, waveDuration = 1

		this.mainTl = new TimelineMax({paused:true}).timeScale(1)

		TweenMax.set(this.$('.icon-dataset-building'), {
			visibility: 'visible'
		})

		TweenMax.set(this.$('.leftFaceGroup'), {
			skewY:30,
			y:52,
			x:-38
		})

		TweenMax.set(this.$('.rightBackFaceGroup'), {
			skewY:30,
			y:75,
			x:74
		})

		TweenMax.set(this.$('.leftBackFaceGroup'), {
			skewY:-30,
			y:145,
			x:-35
		})

		TweenMax.set(this.$('.rightFaceGroup'), {
			skewY:-30,
			y:158,
			x:15
		})

		waveTl.to(leftFaceWave, waveDuration, {
			x:70,
			ease: Linear.easeNone
		})
		.to(rightFaceWave, waveDuration, {
			x:70,
			ease: Linear.easeNone
		},'-='+waveDuration)
		.to(leftBackFaceWave, waveDuration, {
			x:70,
			ease: Linear.easeNone
		},'-='+waveDuration)
		.to(rightBackFaceWave, waveDuration, {
			x:-70,
			ease: Linear.easeNone
		},'-='+waveDuration)

		progressTl.to([leftFaceWave, rightFaceWave], 10, {
			y:-45,
			ease: Linear.easeNone
		})
		.to([leftBackFaceWave, rightBackFaceWave], 6, {
			y:-45,
			ease: Linear.easeNone
		},'-=6')
		.from('.face-wave', 10, {
			alpha:1,
			ease: Linear.easeNone
		},'-=10')

		this.mainTl.add(progressTl)
	},

	/**
	 *
	 * @param {Number} progress 0 to 1
	 * @param {String} [label]
	 * @return {Promise<any>}
	 */
	setProgress: function(progress, label) {
		progress = progress || 0
		return new Promise((resolve) => {
			// Don't try to update the progress if the dataset is currently in an errored state
			if (this.state == Dataset.ERROR) {
				return
			}

			// Make sure the progress value is within 0 - 1 and round it off
			// to one decimal place
			progress = Math.max(Math.min(progress, 1), 0).toFixed(1)

			// If we're setting a progress, that means the dataset is in the loading state
			if (progress > 0 && progress < 1) {
				this.setState(Dataset.LOADING)
			}

			this._applyClasses(progress)

			// If this.options.isQuickset = true then pass TweenMax 0 so that it doesn't
			// animate the first progress fill
			var animate = this.options.isQuickset ? 0 : 1

			// Set the progress of the dataset liquid animation
			TweenMax.to(this.mainTl, animate, {
				progress: progress,
				ease: Back.easeOut.config(0.5),
				onComplete: () => resolve()
			})

			// Set the progress of the horisontal loading bar
			let percent = Math.min(progress * 100, 100)
			this.$('.progress').css('width', percent + '%')
			label && this.$('.loading-text').html(label)
			delete this.options.isQuickset
		})
	},

	setState: function(state) {
		state = state || Dataset.STALE
		this.state = state
		this.$el.removeClass('dataset-state-stale dataset-state-loading dataset-state-ready dataset-state-error')
		if (state == Dataset.STALE) {
			this.$el.addClass('dataset-state-stale')
		}  else if (state == Dataset.LOADING) {
			this.$el.addClass('dataset-state-loading')
		} else if (state == Dataset.READY || state == Dataset.INDEXED) {
			this.$el.addClass('dataset-state-ready')
		} else if (state == Dataset.ERROR) {
			this.$el.addClass('dataset-state-error')
		} else if (state == Dataset.AWAITING_RESPONSE) {
			this.$el.addClass('dataset-state-awaiting-response')
		}
	},

	getState: function() {
		return this.state
	},

	_applyClasses: function(progress) {
		var svg = this.$('.icon-dataset-building')
		svg.removeClass('is-completed is-empty')
		if (progress == 0) {
			svg.addClass('is-empty')
		} else if (progress == 1) {
			this.$el.removeClass('is-quickset')
			svg.addClass('is-completed')
		}
	}
})

export default DatasetBuilding

DatasetBuilding.__filename__ = 'datasets/not-available/dataset-building.js'
